// src/services/calendarService.js
// Calendar Events Service (read model for Work items)
// -----------------------------------------------------------------------------
// Endpoints consumed:
//   GET /calendar/events?from&to&assigneeId&clientId&assigneeName&clientName&type&status&priority&includeCancelled&q
//
// NOTE: Axios baseURL already includes `/api`, so request path here MUST NOT.
// If your baseURL does NOT include `/api`, change the line in `listEvents`
// to use `/api/calendar/events` instead.
//
// Response contract (backend):
//   { success: true, items } or { success:false, error, message }
//
// This service:
//  - Converts local YYYY-MM-DD `from`/`to` → inclusive ISO window (local tz).
//  - Excludes soft-deleted / hidden events client-side (defensive).
//  - Excludes cancelled by default; only includes when includeCancelled === true.
//  - Normalizes each item to always have: { id, workId, start, end, ... }.
//  - Uses occurrenceLocalStart (if present) to group by local day.
//  - Accepts aliases for filters and normalizes them to backend expectations.
//  - NEW: Expands to multiple param aliases so calendar API can match (assignee/assigneeId/assigneeIds[], etc.)
//  - NEW: Optional client-side fallback filtering for assignee/client/q via REACT_APP_CALENDAR_FALLBACK_FILTERS
// -----------------------------------------------------------------------------

import api from '../api/axios';
import { stringify } from 'qs'; // if not installed, replace with URLSearchParams

// ---------- debug toggles ----------
const DEBUG = String(process.env.REACT_APP_CALENDAR_DEBUG || '').toLowerCase() === 'true';
const FALLBACK = String(process.env.REACT_APP_CALENDAR_FALLBACK_FILTERS || '').toLowerCase() === 'true';
const dlog = (...args) => { if (DEBUG) console.log(...args); };

/* ------------------------------ internal utils ------------------------------ */

function cleanParams(obj = {}) {
  const out = {};
  Object.entries(obj).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    if (typeof v === 'string' && v.trim() === '') return;
    if (Array.isArray(v) && v.length === 0) return;
    out[k] = v;
  });
  return out;
}

const toArray = (v) => {
  if (v == null) return undefined;
  if (Array.isArray(v)) return v.filter(Boolean);
  if (typeof v === 'string') {
    // support CSV "a,b,c" OR a single string id
    const arr = v.split(',').map((s) => s.trim()).filter(Boolean);
    return arr.length ? arr : undefined;
  }
  return [v].filter(Boolean);
};

function buildQuery(params = {}) {
  const cleaned = cleanParams(params);
  // Use repeat format so arrays become key=a&key=b (widely supported)
  return stringify(cleaned, { arrayFormat: 'repeat', encode: true });
}

function okOrThrow(data) {
  if (data && data.success === false) {
    const err = new Error(data.message || 'Request failed');
    err.code = data.error || 'request_failed';
    throw err;
  }
  return data;
}

function httpErrorToMessage(err) {
  const data = err?.response?.data;
  const msg = data?.message || err?.message || 'Something went wrong. Please try again.';
  const e = new Error(msg);
  e.code = data?.error || err?.code || 'request_failed';
  e.status = err?.response?.status;
  return e;
}

function toDateSafe(v) {
  if (!v) return v;
  const d = new Date(v);
  return Number.isFinite(d.getTime()) ? d : v;
}

/** YYYY-MM-DD detection (lenient, but ensures 10 chars and digits-with-dashes) */
function isLocalYMD(s) {
  return typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s);
}

/** Convert local YYYY-MM-DD → ISO at local start-of-day */
function ymdStartToISO(ymd) {
  if (!isLocalYMD(ymd)) return ymd;
  const [y, m, d] = ymd.split('-').map((n) => parseInt(n, 10));
  const dt = new Date(y, m - 1, d, 0, 0, 0, 0);
  return dt.toISOString();
}

/** Convert local YYYY-MM-DD → ISO at local end-of-day (inclusive moment) */
function ymdEndToISO(ymd) {
  if (!isLocalYMD(ymd)) return ymd;
  const [y, m, d] = ymd.split('-').map((n) => parseInt(n, 10));
  const dt = new Date(y, m - 1, d, 23, 59, 59, 999);
  return dt.toISOString();
}

/** Defensive normalization for each event. */
function normalizeEvent(evt, { parseDates = true } = {}) {
  if (!evt || typeof evt !== 'object') return evt;

  const workId =
    evt.workId ||
    evt.work_id ||
    evt.meta?.workId ||
    evt.meta?.work_id ||
    evt.work?.id ||
    evt.work?._id ||
    evt.source?.workId ||
    evt.sourceId ||
    undefined;

  const fallbackId =
    evt.recurrenceInstanceKey ||
    (workId ? `${workId}:${evt.start || ''}` : undefined);
  const baseId = evt.id || fallbackId;

  const start = parseDates ? toDateSafe(evt.start) : evt.start;
  const end = parseDates ? toDateSafe(evt.end) : evt.end;

  return {
    ...evt,
    id: baseId,
    workId: workId || evt.workId,
    start,
    end,
  };
}

const CANCELLED_STATUSES = new Set(['cancelled', 'canceled', 'no_show', 'deleted']);
const isCancelledStatus = (s) => CANCELLED_STATUSES.has(String(s || '').toLowerCase());

const isSoftDeletedOrHidden = (e) =>
  Boolean(
    e?.deletedAt ||
    e?.softDeleted ||
    e?.isDeleted ||
    e?.deleted === true ||
    String(e?.visibility || '').toLowerCase() === 'hidden'
  );

/* ------------------------------ normalization ------------------------------- */

/**
 * Normalize calendar filter params to backend expectations, returning a canonical shape.
 * We keep assigneeId/clientId as **arrays** (if present) so we can emit repeated keys.
 */
function normalizeCalendarFilters(raw = {}) {
  const firstNonEmpty = (vals) =>
    vals.find((v) => typeof v === 'string' && v.trim() !== '');

  // IDs (accept alias) → arrays
  const assigneeId = toArray(raw.assigneeId ?? raw.assignee ?? undefined);
  const clientId   = toArray(raw.clientId   ?? raw.client   ?? undefined);

  // name-based filters (trim if provided)
  const assigneeNameRaw = raw.assigneeName ?? raw.assignee_name ?? undefined;
  const clientNameRaw   = raw.clientName   ?? raw.client_name   ?? undefined;
  const assigneeName =
    typeof assigneeNameRaw === 'string' && assigneeNameRaw.trim() !== ''
      ? assigneeNameRaw.trim()
      : undefined;
  const clientName =
    typeof clientNameRaw === 'string' && clientNameRaw.trim() !== ''
      ? clientNameRaw.trim()
      : undefined;

  // search term (aliases) → single string
  const qAlias = firstNonEmpty([raw.q, raw.search, raw.title, raw.query, raw.keyword, raw.term]);
  const q = qAlias ? qAlias.trim() : undefined;

  const status   = toArray(raw.status);
  const priority = toArray(raw.priority);

  // Handle local YYYY-MM-DD → inclusive ISO conversion
  const fromISO = isLocalYMD(raw.from) ? ymdStartToISO(raw.from) : raw.from;
  const toISO   = isLocalYMD(raw.to)   ? ymdEndToISO(raw.to)     : raw.to;

  // only set includeCancelled when explicitly true
  const includeCancelled =
    typeof raw.includeCancelled === 'string'
      ? raw.includeCancelled.toLowerCase() === 'true'
      : raw.includeCancelled === true;

  const normalized = {
    from: fromISO,
    to: toISO,
    assigneeId, // arrays or undefined
    clientId,   // arrays or undefined
    ...(assigneeName ? { assigneeName } : {}),
    ...(clientName ? { clientName } : {}),
    ...(q ? { q } : {}),
    type: raw.type, // leave as-is (server accepts string here)
    status: status && status.length ? status : undefined,
    priority: priority && priority.length ? priority : undefined,
    ...(includeCancelled ? { includeCancelled: true } : {}),
  };

  return cleanParams(normalized);
}

/**
 * Expand normalized params into multiple **alias** keys that various calendar backends use.
 * (We keep original keys too.)
 */
function expandForBackendAliases(n = {}) {
  const out = { ...n };

  const aIds = toArray(n.assigneeId);
  const cIds = toArray(n.clientId);

  if (aIds?.length) {
    // keep original
    out.assigneeId = aIds;
    // common aliases
    out.assignee = aIds;
    out.assigneeIds = aIds;
    out['assigneeIds[]'] = aIds; // some servers expect brackets
    out.assignedTo = aIds;       // occasionally used
    out.assignee_id = aIds;      // snake_case
  }

  if (cIds?.length) {
    out.clientId = cIds;
    out.client = cIds;
    out.clientIds = cIds;
    out['clientIds[]'] = cIds;
    out.client_id = cIds;
  }

  if (n.q) {
    // send multiple aliases so route-specific handlers catch it
    out.q = n.q;
    out.search = n.q;
    out.query = n.q;
    out.term = n.q;
    out.title = n.q;
  }

  return out;
}

/* --------------------------------- API -------------------------------------- */

/**
 * Default time window for calendar queries.
 * @param {number} daysBack  default 7
 * @param {number} daysForward default 60
 * @returns {{from: string, to: string}} ISO strings
 */
export function defaultRange(daysBack = 7, daysForward = 60) {
  const now = Date.now();
  const from = new Date(now - daysBack * 24 * 3600 * 1000).toISOString();
  const to = new Date(now + daysForward * 24 * 3600 * 1000).toISOString();
  return { from, to };
}

/**
 * List calendar events in a window.
 */
export async function listEvents(params = {}, options = {}) {
  const { parseDates = true, signal } = options;

  try {
    // Merge defaults; then normalize & alias all filters (incl. YMD→ISO conversion)
    if (DEBUG) dlog('[calendar:paramsRaw]', params);
    const baseParams = { ...defaultRange(), ...params };
    const normalized = normalizeCalendarFilters(baseParams);
    const expanded   = expandForBackendAliases(normalized);

    dlog('[calendar:paramsNormalized]', normalized);
    dlog('[calendar:expandedParams]', {
      from: expanded.from,
      to: expanded.to,
      keys: Object.keys(expanded).sort(),
      assigneeId: expanded.assigneeId,
      clientId: expanded.clientId,
      q: expanded.q,
    });

    // Build query string: array fields use repeat format (status=a&status=b)
    const query = buildQuery(expanded);
    const url = `/calendar/events?${query}`;
    dlog('[calendar:fetchUrl]', url);

    // Axios baseURL includes '/api'
    const res = await api.get(url, { signal });
    const payload = okOrThrow(res.data);

    // Robustly read array
    let items = Array.isArray(payload?.items)
      ? payload.items
      : Array.isArray(payload?.data)
      ? payload.data
      : Array.isArray(payload)
      ? payload
      : [];

    // Defensive: drop soft-deleted/hidden
    items = items.filter((e) => !isSoftDeletedOrHidden(e));

    // Exclude cancelled by default (either explicit isCancelled flag or status)
    const includeCancelled = Boolean(normalized.includeCancelled);
    if (!includeCancelled) {
      items = items.filter((e) =>
        e?.isCancelled === true ? false : !isCancelledStatus(e?.status)
      );
    }

    // --- Optional local fallback filtering (assigneeId, clientId, q) ---
    if (FALLBACK) {
      const before = items.length;
      const aSet = new Set(toArray(normalized.assigneeId) || []);
      const cSet = new Set(toArray(normalized.clientId) || []);
      const qStr = (normalized.q || '').toLowerCase();

      const hasA = aSet.size > 0;
      const hasC = cSet.size > 0;
      const hasQ = qStr.length > 0;

      const getIds = (e) => {
        const arr = [];
        if (e.assigneeId) arr.push(e.assigneeId);
        if (Array.isArray(e.assigneeIds)) arr.push(...e.assigneeIds);
        if (Array.isArray(e.assignees)) arr.push(...e.assignees.map(x => x?.id || x?._id || x));
        if (e.assignedTo) {
          if (Array.isArray(e.assignedTo)) arr.push(...e.assignedTo.map(x => x?.id || x?._id || x));
          else arr.push(e.assignedTo);
        }
        if (e.work?.assigneeId) arr.push(e.work.assigneeId);
        if (Array.isArray(e.work?.assigneeIds)) arr.push(...e.work.assigneeIds);
        return arr.filter(Boolean).map(String);
      };

      const getClientIds = (e) => {
        const arr = [];
        if (e.clientId) arr.push(e.clientId);
        if (e.client_id) arr.push(e.client_id);
        if (e.work?.clientId) arr.push(e.work.clientId);
        if (e.work?.client?._id) arr.push(e.work.client._id);
        if (e.work?.client?.id) arr.push(e.work.client.id);
        return arr.filter(Boolean).map(String);
      };

      const matchesAssignee = (e) =>
        !hasA || getIds(e).some((id) => aSet.has(String(id)));

      const matchesClient = (e) =>
        !hasC || getClientIds(e).some((id) => cSet.has(String(id)));

      const matchesQ = (e) => {
        if (!hasQ) return true;
        const hay = [
          e.title, e.name, e.summary, e.description,
          e.work?.title, e.work?.name, e.work?.summary,
        ].filter(Boolean).join(' ').toLowerCase();
        return hay.includes(qStr);
      };

      const afterA = items.filter(matchesAssignee);
      const afterC = afterA.filter(matchesClient);
      const afterQ = afterC.filter(matchesQ);

      dlog('[calendar:postFilter.counts]', { before, afterAssignee: afterA.length, afterClient: afterC.length, afterQ: afterQ.length });
      items = afterQ;
    }

    // Normalize shape & dates
    items = items.map((e) => normalizeEvent(e, { parseDates }));

    dlog('[calendar:responseCount]', items.length);
    if (DEBUG && items.length) {
      const sample = items[0];
      dlog('[calendar:responseSample0]', {
        id: sample?.id,
        workId: sample?.workId,
        start: sample?.start,
        end: sample?.end,
        status: sample?.status,
      });
    }

    return { items };
  } catch (err) {
    const e = httpErrorToMessage(err);
    dlog('[calendar:error]', { message: e.message, code: e.code, status: e.status });
    throw e;
  }
}

/**
 * Group events by a local YYYY-MM-DD key.
 * Prefers occurrenceLocalStart from the server (already local); falls back to local Date.
 */
export function groupEventsByDay(events = [], { parseDates = false } = {}) {
  const out = {};
  for (const ev of events) {
    const e = parseDates ? normalizeEvent(ev, { parseDates: true }) : ev;

    let key =
      typeof e?.occurrenceLocalStart === 'string' &&
      e.occurrenceLocalStart.length >= 10
        ? e.occurrenceLocalStart.slice(0, 10)
        : null;

    if (!key) {
      const start = e?.start instanceof Date ? e.start : new Date(e?.start);
      if (!Number.isFinite(start?.getTime?.())) continue;
      key = [
        start.getFullYear(),
        String(start.getMonth() + 1).padStart(2, '0'),
        String(start.getDate()).padStart(2, '0'),
      ].join('-');
    }

    if (!out[key]) out[key] = [];
    out[key].push(e);
  }
  return out;
}

/**
 * Compute duration in minutes for an event.
 */
export function computeDurationMinutes(event) {
  if (!event) return undefined;
  const start = event.start instanceof Date ? event.start : new Date(event.start);
  const end = event.end instanceof Date ? event.end : new Date(event.end);
  if (!Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime())) return undefined;
  const ms = end - start;
  return Math.max(0, Math.round(ms / 60000));
}
